// Desktop Bridge, Hardware Tokens & Local Storage Bridge for Deep Research Hub (Tauri & Web)

export interface DesktopModuleState {
  enabledModuleIds: string[];
  offlineMode: boolean;
  autoSaveDirectory?: string;
  theme: 'light' | 'dark' | 'system';
}

export interface HardwareTokenInfo {
  detected: boolean;
  token_type: string;
  provider: string;
  hardware_id: string;
  ready_for_signing: boolean;
}

export interface SaveFileResult {
  success: boolean;
  path?: string;
  filename: string;
  size_bytes?: number;
  message: string;
}

const DEFAULT_ENABLED_MODULES = [
  'tax-pn-unblocker-auditor',
  'tax-transfer-pricing-doc-architect',
  'tax-ksef-vat-auditor',
  'contract-auditor',
  'eu-counterparty-auditor',
  'tender-analyst',
  'dstu-order-constructor',
  'public-accountant-kekv',
  'office-spreadsheet-analyst',
  'customs-sad-md2-builder-xml'
];

export const isDesktopApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !!(window as any).__TAURI__ || !!(window as any).__TAURI_METADATA__;
};

export const getDesktopModules = (): string[] => {
  try {
    const raw = localStorage.getItem('drh_desktop_enabled_modules');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (err) {
    console.warn('Error reading desktop enabled modules:', err);
  }
  return DEFAULT_ENABLED_MODULES;
};

export const toggleDesktopModule = (agentId: string, enabled: boolean): string[] => {
  const current = getDesktopModules();
  let updated: string[];
  if (enabled) {
    updated = Array.from(new Set([...current, agentId]));
  } else {
    updated = current.filter(id => id !== agentId);
  }
  try {
    localStorage.setItem('drh_desktop_enabled_modules', JSON.stringify(updated));
  } catch (err) {
    console.warn('Error saving desktop modules:', err);
  }
  return updated;
};

export const isDesktopModuleActive = (agentId: string): boolean => {
  if (!isDesktopApp()) return true;
  const modules = getDesktopModules();
  return modules.includes(agentId);
};

export const enableAllDesktopModules = (allAgentIds: string[]): void => {
  try {
    localStorage.setItem('drh_desktop_enabled_modules', JSON.stringify(allAgentIds));
  } catch (err) {
    console.warn('Error enabling all modules:', err);
  }
};

/**
 * Native File Saver: Saves document to local disk in Tauri, or initiates browser download
 */
export const saveDocumentLocally = async (
  filename: string,
  blobOrBase64: Blob | string,
  subfolder?: string
): Promise<SaveFileResult> => {
  // If running inside Tauri desktop app
  if (isDesktopApp() && (window as any).__TAURI__?.invoke) {
    try {
      let base64Data: string;
      if (typeof blobOrBase64 === 'string') {
        base64Data = blobOrBase64.replace(/^data:.*?;base64,/, '');
      } else {
        const buffer = await blobOrBase64.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        base64Data = window.btoa(binary);
      }

      const res = await (window as any).__TAURI__.invoke('save_document_to_disk', {
        filename,
        contentBase64: base64Data,
        subfolder: subfolder || null
      });

      return res;
    } catch (err: any) {
      console.warn('Tauri native save error, falling back to browser download:', err);
    }
  }

  // Web fallback: classic browser download
  if (typeof window !== 'undefined') {
    let url: string;
    let createdUrl = false;
    if (typeof blobOrBase64 === 'string') {
      url = blobOrBase64.startsWith('data:') ? blobOrBase64 : `data:application/octet-stream;base64,${blobOrBase64}`;
    } else {
      url = URL.createObjectURL(blobOrBase64);
      createdUrl = true;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    if (createdUrl) {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    return {
      success: true,
      filename,
      message: 'Файл завантажено через браузер'
    };
  }

  return {
    success: false,
    filename,
    message: 'Неможливо зберегти файл'
  };
};

/**
 * Reveal file in native file manager (Explorer on Windows, Finder on macOS)
 */
export const revealInExplorer = async (filePath: string): Promise<void> => {
  if (isDesktopApp() && (window as any).__TAURI__?.invoke) {
    try {
      await (window as any).__TAURI__.invoke('reveal_in_file_manager', { filePath });
    } catch (err) {
      console.warn('Error revealing file in explorer:', err);
    }
  }
};

/**
 * Open file in default OS application (MS Word, Excel, Acrobat)
 */
export const openInDefaultApp = async (filePath: string): Promise<void> => {
  if (isDesktopApp() && (window as any).__TAURI__?.invoke) {
    try {
      await (window as any).__TAURI__.invoke('open_file_default_app', { filePath });
    } catch (err) {
      console.warn('Error opening file with default app:', err);
    }
  }
};

/**
 * Detect connected USB Hardware Tokens (Almaz-1K, Crystal, Certum eIDAS)
 */
export const detectHardwareTokens = async (): Promise<HardwareTokenInfo[]> => {
  if (isDesktopApp() && (window as any).__TAURI__?.invoke) {
    try {
      const tokens = await (window as any).__TAURI__.invoke('detect_connected_tokens');
      return tokens;
    } catch (err) {
      console.warn('Error probing hardware tokens:', err);
    }
  }

  // Web mode fallback simulation
  return [
    {
      detected: true,
      token_type: 'Апаратний USB-токен КЕП (ДСТУ 4163:2020 / Алмаз-1К / Кристал)',
      provider: 'Web Crypto PKCS#11 / Дія.Підпис',
      hardware_id: 'USB\\VID_IIT_ALMAZ',
      ready_for_signing: true
    }
  ];
};
